// ─── Hero: Split Layout ───────────────────────────────────────
// Image on left, text on right. Elegant asymmetric feel.

import React from 'react';
import { AnimatedSection, StaggerContainer } from '../../../lib/animations/AnimatedSection';

export default function HeroSplit({ section, data, theme, settings, rsvpRef }) {
  const { title, subtitle, dateText, backgroundImage, introLabel, ctaText, ctaTarget } = data;
  const anim = section.animation || {};

  const scrollToTarget = () => {
    if (ctaTarget && ctaTarget !== 'rsvp') {
      const el = document.querySelector(`[id^="${ctaTarget}-"]`) || document.querySelector(`[data-section-type="${ctaTarget}"]`);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
    }
    rsvpRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section className="relative min-h-screen flex flex-col md:flex-row" data-testid="hero-section">
      {/* Image half */}
      <AnimatedSection preset={anim.image || 'zoom-soft'} className="relative w-full md:w-1/2 min-h-[50vh] md:min-h-screen">
        {backgroundImage ? (
          <img src={backgroundImage} alt="" className="absolute inset-0 w-full h-full object-cover" loading="eager" />
        ) : (
          <div className="absolute inset-0" style={{ background: theme.accentSoft }} />
        )}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, transparent 60%, rgba(0,0,0,0.08) 100%)' }} />
      </AnimatedSection>

      {/* Text half */}
      <div
        className="w-full md:w-1/2 flex flex-col items-center justify-center px-8 py-16 md:py-0"
        style={{ backgroundColor: theme.bg }}
      >
        <div className="max-w-xs text-center md:text-left">
          <StaggerContainer preset={anim.title || 'stagger-text'}>
            <p
              className="text-xs tracking-[0.35em] uppercase mb-6"
              style={{ color: theme.accent, fontFamily: theme.eyebrowFont }}
            >
              {introLabel || 'We\'re getting married'}
            </p>
            <h1
              className="text-4xl sm:text-5xl leading-tight mb-4 font-light"
              style={{ color: theme.textPrimary, fontFamily: theme.headingFont }}
            >
              {title || 'Our Wedding'}
            </h1>
            <div className="w-12 h-px mb-4 md:mx-0 mx-auto" style={{ background: theme.accent }} />
            <p className="text-sm tracking-wider" style={{ color: theme.textSecondary, fontFamily: theme.eyebrowFont }}>
              {dateText || ''}
            </p>
            {subtitle && (
              <p className="text-xs mt-2 tracking-wider" style={{ color: theme.textSecondary, opacity: 0.7, fontFamily: theme.eyebrowFont }}>
                {subtitle}
              </p>
            )}
          </StaggerContainer>
          {ctaText && (
          <AnimatedSection preset={anim.cta || 'fade-up'} delay={0.5}>
            <button
              onClick={scrollToTarget}
              className="mt-10 px-8 py-3 rounded-full text-xs font-medium tracking-widest uppercase transition-all duration-300 hover:scale-105"
              style={{
                background: theme.buttonBg,
                color: theme.buttonText,
                fontFamily: theme.eyebrowFont,
              }}
              data-testid="hero-rsvp-cta"
            >
              {ctaText}
            </button>
          </AnimatedSection>
          )}
        </div>
      </div>
    </section>
  );
}
